<template>
  <Teleport to=".app-shell">
    <div class="win">
      <div v-if="phase === 'flight'" class="scrim"></div>

      <div v-if="phase === 'flight'" class="stage" aria-hidden="true">
        <div class="rays"></div>
        <div class="flash"></div>
        <span class="ring ring-a"></span>
        <span class="ring ring-b"></span>
        <span class="ring ring-c"></span>
        <span
          v-for="spark in sparks"
          :key="spark.id"
          class="spark"
          :style="{
            '--sx': spark.x,
            '--sy': spark.y,
            '--size': spark.size,
            animationDelay: spark.delay,
          }"
        ></span>
        <div class="hero" :style="heroStyle">
          <span class="halo"></span>
          <img :src="cover" alt="">
          <span class="glint"></span>
        </div>
      </div>

      <dialog
        v-else
        ref="dialogEl"
        class="panel"
        :aria-labelledby="titleId"
        :aria-describedby="leadId"
        @cancel.prevent="close"
      >
        <p class="kicker">{{ t('missions.packWinKicker') }}</p>
        <PackCover :src="cover" :alt="t('missions.packPrize')" size="md" />
        <h2 :id="titleId">{{ t('missions.packWinTitle') }}</h2>
        <p :id="leadId" class="lead">
          {{ lead }}
        </p>
        <div class="actions">
          <button type="button" class="cta" @click="openPacks">
            {{ t('missions.packWinOpen') }}
          </button>
          <button
            ref="closeBtn"
            type="button"
            class="ghost"
            @click="close"
          >
            {{ t('missions.packWinClose') }}
          </button>
        </div>
      </dialog>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { MISSION_PACK_THRESHOLD, RANKED_DAILY_COUNT } from '@shared/index'
import { PackSfx } from '../game/audio/PackSfx'
import PackCover from './PackCover.vue'
import { useI18n } from '../i18n/t'

const FLIGHT_MS = 2600

const props = withDefaults(defineProps<{
  cover: string
  dx: number
  dy: number
  variant?: 'daily' | 'ranked' | 'collection'
  packCount?: number
  /** Ranked ladder step this pack came from. */
  at?: number
}>(), {
  variant: 'daily',
  packCount: 1,
  at: 0,
})

const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
const lead = computed(() => {
  if (props.variant === 'ranked') {
    const n = props.at > 0 ? props.at : RANKED_DAILY_COUNT
    if (props.packCount > 1) {
      return t('missions.rankedPackWinMany', { n, packs: props.packCount })
    }
    return t('missions.rankedPackWinLead', { n })
  }
  if (props.variant === 'collection') {
    const n = props.packCount
    return n === 1
      ? t('missions.collection.packWinOne')
      : t('missions.collection.packWinMany', { n })
  }
  return t('missions.packWinLead', { n: MISSION_PACK_THRESHOLD })
})
const router = useRouter()
const phase = ref<'flight' | 'dialog'>('flight')
const titleId = 'mission-pack-win-title'
const leadId = 'mission-pack-win-lead'
const dialogEl = ref<HTMLDialogElement | null>(null)
const closeBtn = ref<HTMLButtonElement | null>(null)
let flightTimer: ReturnType<typeof setTimeout> | null = null
let stingTimer: ReturnType<typeof setTimeout> | null = null
let prevBodyOverflow = ''
let prevRootOverflow = ''

const sparks = Array.from({ length: 18 }, (_, i) => {
  const wave = i < 9 ? 0 : 1
  const index = i % 9
  const angle = (index / 9) * Math.PI * 2 + wave * 0.35
  const dist = (wave === 0 ? 128 : 196) + (index % 3) * 18
  return {
    id: i,
    delay: `${(wave === 0 ? 820 : 1320) + (index % 5) * 40}ms`,
    x: `${Math.round(Math.cos(angle) * dist)}px`,
    y: `${Math.round(Math.sin(angle) * dist)}px`,
    size: wave === 0 ? '10px' : '7px',
  }
})

const heroStyle = computed(() => ({
  '--dx': `${props.dx}px`,
  '--dy': `${props.dy}px`,
  animationDuration: `${FLIGHT_MS}ms`,
}))

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function showDialog(): void {
  if (phase.value === 'dialog') return
  phase.value = 'dialog'
  void nextTick(() => {
    dialogEl.value?.showModal()
    closeBtn.value?.focus()
  })
}

function close(): void {
  emit('close')
}

function openPacks(): void {
  emit('close')
  void router.push({ name: 'packs' })
}

function onKey(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || phase.value !== 'flight') return
  event.preventDefault()
  showDialog()
}

onMounted(() => {
  prevBodyOverflow = document.body.style.overflow
  prevRootOverflow = document.documentElement.style.overflow
  document.body.style.overflow = 'hidden'
  document.documentElement.style.overflow = 'hidden'
  window.addEventListener('keydown', onKey)
  if (prefersReducedMotion()) {
    showDialog()
    return
  }
  PackSfx.play('whoosh')
  stingTimer = window.setTimeout(() => {
    PackSfx.play('ultra')
  }, 980)
  flightTimer = window.setTimeout(() => {
    PackSfx.play('summary')
    showDialog()
  }, FLIGHT_MS)
})

onUnmounted(() => {
  if (dialogEl.value?.open) dialogEl.value.close()
  document.body.style.overflow = prevBodyOverflow
  document.documentElement.style.overflow = prevRootOverflow
  window.removeEventListener('keydown', onKey)
  if (flightTimer) clearTimeout(flightTimer)
  if (stingTimer) clearTimeout(stingTimer)
})
</script>

<style scoped>
.win {
  position: fixed;
  inset: 0;
  z-index: 120;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: max(16px, env(safe-area-inset-top))
    max(16px, env(safe-area-inset-right))
    max(16px, env(safe-area-inset-bottom))
    max(16px, env(safe-area-inset-left));
}

.scrim {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      circle at 50% 42%,
      color-mix(in srgb, var(--md-sys-color-primary) 42%, transparent),
      transparent 46%
    ),
    rgba(4, 6, 16, 0.84);
  animation: scrim-in var(--md-motion-medium) var(--md-motion-standard) both;
}

.stage {
  position: absolute;
  inset: 0;
  pointer-events: none;
  animation: stage-shake 0.42s linear 0.95s 1;
}

.rays {
  position: absolute;
  left: 50%;
  top: 42%;
  width: 160vmax;
  height: 160vmax;
  background: repeating-conic-gradient(
    from 0deg,
    transparent 0 11deg,
    color-mix(in srgb, var(--md-sys-color-primary) 78%, white) 11deg 13.5deg
  );
  -webkit-mask-image: radial-gradient(circle, #000 0 12%, transparent 58%);
  mask-image: radial-gradient(circle, #000 0 12%, transparent 58%);
  transform: translate(-50%, -50%);
  animation: rays-spin 2.6s linear both;
  pointer-events: none;
}

.flash {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 42%, #fff, transparent 52%);
  animation: flash-pop 2.6s ease-out forwards;
  pointer-events: none;
}

.ring {
  position: absolute;
  left: 50%;
  top: 42%;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  border: 3px solid color-mix(in srgb, var(--md-sys-color-primary) 75%, white);
  transform: translate(-50%, -50%) scale(0.2);
  opacity: 0;
  pointer-events: none;
}

.ring-a {
  animation: ring-burst 0.95s var(--md-motion-emphasized) 0.72s forwards;
}

.ring-b {
  animation: ring-burst 1.05s var(--md-motion-emphasized) 1.08s forwards;
}

.ring-c {
  animation: ring-burst 1.15s var(--md-motion-emphasized) 1.48s forwards;
}

.spark {
  position: absolute;
  left: 50%;
  top: 42%;
  width: var(--size, 8px);
  height: var(--size, 8px);
  border-radius: 50%;
  background: color-mix(in srgb, var(--md-sys-color-primary) 35%, white);
  box-shadow: 0 0 10px color-mix(in srgb, var(--md-sys-color-primary) 70%, white);
  animation: spark-out 0.9s var(--md-motion-emphasized) forwards;
  pointer-events: none;
}

.hero {
  position: absolute;
  left: 50%;
  top: 42%;
  width: min(300px, 74vw);
  aspect-ratio: 5 / 7;
  animation-name: pack-flight;
  animation-timing-function: cubic-bezier(0.16, 0.84, 0.24, 1);
  animation-fill-mode: both;
  filter: drop-shadow(0 22px 28px rgba(0, 0, 0, 0.55));
}

.hero img {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 8px;
}

.halo {
  position: absolute;
  inset: -22%;
  z-index: 0;
  border-radius: 40%;
  background: radial-gradient(
    circle,
    color-mix(in srgb, var(--md-sys-color-primary) 80%, white),
    transparent 68%
  );
  filter: blur(10px);
  animation: halo-pulse 0.7s var(--md-motion-standard) infinite;
}

.glint {
  position: absolute;
  inset: 8% 10%;
  z-index: 2;
  border-radius: 8px;
  background: linear-gradient(
    115deg,
    transparent 28%,
    rgba(255, 255, 255, 0.82) 48%,
    transparent 64%
  );
  mix-blend-mode: screen;
  animation: glint 2.6s ease-in-out forwards;
  pointer-events: none;
}

.panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(440px, calc(100vw - 32px));
  max-height: calc(100dvh - 32px);
  margin: auto;
  overflow-y: auto;
  padding: 28px 24px 24px;
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--md-sys-color-primary) 45%, transparent);
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-2);
  color: var(--md-sys-color-on-surface);
  text-align: center;
  animation: panel-in var(--md-motion-medium) var(--md-motion-emphasized) both;
}

.panel::backdrop {
  background: var(--md-scrim);
}

.kicker {
  margin: 0 0 12px;
  color: var(--md-sys-color-primary);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-size: 11px;
  font-weight: 500;
}

h2 {
  margin: 14px 0 10px;
  font-size: 28px;
  font-weight: 400;
  color: var(--md-sys-color-on-surface);
}

.lead {
  margin: 0 0 22px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 15px;
  line-height: 1.5;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  width: 100%;
}

.cta,
.ghost {
  min-height: 44px;
  padding: 10px 16px;
  border-radius: 8px;
  font-family: inherit;
  font-weight: 700;
  font-size: 14px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.cta {
  border: 0;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.cta:hover,
.cta:focus-visible {
  filter: brightness(1.08);
}

.ghost {
  border: 1px solid var(--md-sys-color-outline);
  background: transparent;
  color: var(--md-sys-color-on-surface);
}

.ghost:hover,
.ghost:focus-visible {
  background: var(--md-state-layer);
}

@keyframes scrim-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes rays-spin {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) rotate(0deg) scale(0.4);
  }
  18% { opacity: 0.9; }
  78% { opacity: 0.72; }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) rotate(78deg) scale(1);
  }
}

@keyframes flash-pop {
  0%, 34% { opacity: 0; }
  42% { opacity: 0.88; }
  58%, 100% { opacity: 0; }
}

@keyframes ring-burst {
  0% {
    opacity: 0.95;
    transform: translate(-50%, -50%) scale(0.25);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(3.6);
  }
}

@keyframes spark-out {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.2);
  }
  18% { opacity: 1; }
  100% {
    opacity: 0;
    transform: translate(calc(-50% + var(--sx)), calc(-50% + var(--sy))) scale(1);
  }
}

@keyframes pack-flight {
  0% {
    opacity: 0;
    transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0.14) rotate(-26deg);
  }
  12% { opacity: 1; }
  30% {
    transform: translate(calc(-50% - 22vw), -70%) scale(1.05) rotate(14deg);
  }
  46% {
    transform: translate(calc(-50% + 18vw), -38%) scale(1.22) rotate(-12deg);
  }
  62% {
    transform: translate(-50%, -52%) scale(1.08) rotate(6deg);
  }
  78% {
    opacity: 1;
    transform: translate(-50%, -78%) scale(1.28) rotate(-6deg);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -180%) scale(0.2) rotate(20deg);
  }
}

@keyframes halo-pulse {
  0%, 100% { opacity: 0.55; transform: scale(0.92); }
  50% { opacity: 1; transform: scale(1.08); }
}

@keyframes glint {
  0%, 28% { transform: translateX(-120%); opacity: 0; }
  42% { opacity: 1; }
  58% { transform: translateX(120%); opacity: 0; }
  100% { transform: translateX(120%); opacity: 0; }
}

@keyframes stage-shake {
  0%, 100% { transform: translate(0, 0); }
  20% { transform: translate(-7px, 3px); }
  40% { transform: translate(8px, -3px); }
  60% { transform: translate(-5px, -2px); }
  80% { transform: translate(4px, 2px); }
}

@keyframes panel-in {
  from {
    opacity: 0;
    transform: translateY(18px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (max-height: 500px) and (max-width: 1100px) {
  .panel {
    padding: 16px 18px;
  }

  h2 {
    margin-top: 8px;
    font-size: 22px;
  }

  .lead {
    margin-bottom: 14px;
    font-size: 14px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .scrim,
  .stage,
  .rays,
  .flash,
  .ring,
  .spark,
  .hero,
  .halo,
  .glint,
  .panel {
    animation: none;
  }
}
</style>
